// 卡密库存 — 按 SKU 的 available/assigned/void 计数 + 批量导入 (h5-benefit-store task 2.3).
// 运营自助入口: 采购来的卡密 (爱奇艺月卡兑换码等) 粘贴导入, 低库存红色预警.
//
// 后端: handlers/cardkey/admin.go
//   GET  /admin/v1/cardkeys/stats?app_id=
//   POST /admin/v1/cardkeys/import {sku_id, codes_text}

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { ImportOutlined, SyncOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ResourceList } from '@/components/ResourceList';
import { listApps, type App } from '@/api/apps';
import { listSKUs, type SKU } from '@/api/skus';
import {
  importCardKeys,
  listCardKeyDiscList,
  listCardKeyStats,
  type CardKeyDiscListItem,
  type CardKeySKUStat,
  type ImportCardKeysResult,
} from '@/api/cardkeys';
import { formatDate } from '@/utils/format';

/** SKU config.fulfillment.provider (卡密类 SKU = 'cardkey' / 'card-api'). */
function fulfillmentProvider(sku: SKU): string {
  const cfg = sku.config as Record<string, unknown> | null | undefined;
  const ff = cfg?.fulfillment as { provider?: string } | undefined;
  return ff?.provider ?? '';
}

/** SKU config.fulfillment.params.card_type (card-api SKU 的卡种). */
function skuCardType(sku: SKU): string {
  const cfg = sku.config as Record<string, unknown> | null | undefined;
  const ff = cfg?.fulfillment as { params?: Record<string, unknown> } | undefined;
  const ct = ff?.params?.card_type;
  return ct == null ? '' : String(ct);
}

const CARD_TYPE_LABEL: Record<string, string> = {
  '1': '单卡卡密',
  '2': '卡号+密码',
  '3': '网址链接',
  '4': '卡密+兑换地址',
};

/** 来源标签: 供应商 (card-api) / 人工导入 / 混合. */
function sourceLabel(row: CardKeySKUStat): { text: string; color: string } {
  if (row.has_supplier && row.has_manual) return { text: '混合', color: 'purple' };
  if (row.has_supplier) return { text: '供应商', color: 'blue' };
  if (row.has_manual) return { text: '人工导入', color: 'default' };
  return { text: '-', color: 'default' };
}

/** 给每列挂 onCell: low_stock 或临期 行整行标红 (临期告警). */
function withLowStockMark(columns: ColumnsType<CardKeySKUStat>): ColumnsType<CardKeySKUStat> {
  return columns.map((col) => ({
    ...col,
    onCell: (row: CardKeySKUStat) =>
      row.low_stock || row.expiring_soon > 0 ? { style: { color: '#cf1322' } } : {},
  }));
}

export function CardKeysListPage() {
  // ---- App 选择器 (stats 按 App 维度, 后端必须带 app_id; 参照 skus/List) ----
  const [apps, setApps] = useState<App[]>([]);
  const [appId, setAppId] = useState<number | undefined>(undefined);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    listApps({ page_size: 100 })
      .then((res) => {
        setApps(res.data);
        const first = res.data.find((a) => a.status === 'active') ?? res.data[0];
        if (first) setAppId((prev) => prev ?? first.id);
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '加载 App 列表失败'));
  }, []);

  const appOptions = useMemo(
    () =>
      apps.map((a) => ({
        value: a.id,
        label: `#${a.id} ${a.name} (${a.slug})`,
      })),
    [apps],
  );

  // ---- 该 App 的 SKU 列表 (导入 Modal 选 SKU 用) ----
  const [skus, setSkus] = useState<SKU[]>([]);
  useEffect(() => {
    if (!appId) return;
    listSKUs({ app_id: appId })
      .then((res) => setSkus(res.data))
      .catch(() => setSkus([]));
  }, [appId]);

  const skuOptions = useMemo(
    () =>
      skus.map((s) => ({
        value: s.id,
        label: `#${s.id} ${s.name} (${s.code})${
          fulfillmentProvider(s) === 'cardkey' ? ' · 卡密' : ''
        }`,
      })),
    [skus],
  );

  // ---- sku_id → 卡种 (card-api SKU 的 config.fulfillment.params.card_type) ----
  const cardTypeBySku = useMemo(() => {
    const m: Record<number, string> = {};
    for (const s of skus) m[s.id] = skuCardType(s);
    return m;
  }, [skus]);

  // ---- 同步商品折扣价 (card-api disclist, 只读; 供应商限流 10s/次, 手动触发) ----
  const [discOpen, setDiscOpen] = useState(false);
  const [discLoading, setDiscLoading] = useState(false);
  const [discItems, setDiscItems] = useState<CardKeyDiscListItem[]>([]);

  const openDiscList = async () => {
    setDiscOpen(true);
    setDiscLoading(true);
    try {
      const res = await listCardKeyDiscList();
      setDiscItems(res.data);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '同步失败 (card-api 未配置或限流)');
    } finally {
      setDiscLoading(false);
    }
  };

  // ---- 导入 Modal ----
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportCardKeysResult | null>(null);
  const [form] = Form.useForm();

  const openImport = () => {
    setImportResult(null);
    form.resetFields();
    setImportOpen(true);
  };

  const handleImport = async () => {
    let v: { sku_id: number; codes_text: string };
    try {
      v = await form.validateFields();
    } catch {
      return; // 表单校验失败, antd 已高亮
    }
    setImporting(true);
    try {
      const res = await importCardKeys(v.sku_id, v.codes_text);
      setImportResult(res);
      message.success(`导入完成: 新入库 ${res.imported} 条, 重复跳过 ${res.duplicated} 条`);
      form.setFieldsValue({ codes_text: '' }); // 清空文本, 方便连续导入下一批
      setReloadKey((k) => k + 1); // 刷新库存列表
    } catch (e) {
      message.error(e instanceof Error ? e.message : '导入失败');
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <Space style={{ marginBottom: 16 }} align="center">
        <Typography.Text strong>App:</Typography.Text>
        <Select
          value={appId}
          options={appOptions}
          onChange={(v) => setAppId(v)}
          style={{ minWidth: 280 }}
          placeholder="选择 App"
          showSearch
          optionFilterProp="label"
        />
      </Space>

      {!appId && (
        <Alert
          type="info"
          showIcon
          message="请选择 App 后查看该 App 的卡密库存"
          style={{ marginBottom: 16 }}
        />
      )}

      {appId && (
        <ResourceList<CardKeySKUStat>
          key={`${appId}:${reloadKey}`}
          title="卡密库存"
          description="按 SKU 汇总的卡密计数: 可用 / 已分配 / 作废. 可用 < 10 或有临期卡视为需关注 (整行标红), 请及时补充导入. 卡密类 SKU (cardkey 人工导入 / card-api 供应商换卡) 支付后自动发卡."
          rowKey="sku_id"
          pagination="offset"
          actions={[
            { label: '导入卡密', icon: <ImportOutlined />, onClick: openImport },
            { label: '同步商品折扣价', icon: <SyncOutlined />, onClick: () => void openDiscList() },
          ]}
          fetcher={async ({ page, pageSize }) => {
            // 后端一次性返回该 App 全部 SKU 的汇总行 — 合成 total 支持客户端分页.
            const res = await listCardKeyStats(appId);
            return { ...res, page: { page, page_size: pageSize, total: res.data.length } };
          }}
          columns={withLowStockMark([
            { title: 'SKU ID', dataIndex: 'sku_id', width: 90 },
            { title: 'Code', dataIndex: 'sku_code', render: (v: string) => <Tag>{v}</Tag> },
            { title: '名称', dataIndex: 'sku_name' },
            {
              title: '卡种',
              key: 'card_type',
              render: (_, row) => {
                const ct = cardTypeBySku[row.sku_id];
                if (!ct) return <Typography.Text type="secondary">-</Typography.Text>;
                return <Tag>{CARD_TYPE_LABEL[ct] ?? `类型${ct}`}</Tag>;
              },
            },
            {
              title: '来源',
              key: 'source',
              render: (_, row) => {
                const s = sourceLabel(row);
                return <Tag color={s.color}>{s.text}</Tag>;
              },
            },
            {
              title: '可用',
              dataIndex: 'available',
              align: 'right',
              render: (v: number, row) => (
                <Typography.Text strong type={row.low_stock ? 'danger' : undefined}>
                  {v}
                </Typography.Text>
              ),
            },
            { title: '已分配', dataIndex: 'assigned', align: 'right' },
            { title: '作废', dataIndex: 'void', align: 'right' },
            {
              title: '最近有效期',
              dataIndex: 'nearest_valid_to',
              render: (v: string | null | undefined) =>
                v ? formatDate(v) : <Typography.Text type="secondary">-</Typography.Text>,
            },
            {
              title: '临期 (7天)',
              dataIndex: 'expiring_soon',
              align: 'right',
              render: (v: number) =>
                v > 0 ? <Tag color="red">{v} 张临期</Tag> : <Typography.Text type="secondary">-</Typography.Text>,
            },
            {
              title: '库存状态',
              dataIndex: 'low_stock',
              render: (v: boolean) =>
                v ? <Tag color="red">低库存</Tag> : <Tag color="green">充足</Tag>,
            },
          ])}
        />
      )}

      {/* ---- 导入卡密 ---- */}
      <Modal
        title="导入卡密"
        open={importOpen}
        onOk={handleImport}
        confirmLoading={importing}
        onCancel={() => setImportOpen(false)}
        okText="导入"
        cancelText="关闭"
        forceRender
        width={560}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="仅卡密类商品需要导入 (SKU 需在「商业 SKU」页配置履约 provider = cardkey). 一行一码, 空行忽略, 与库内重复的 code 自动跳过."
        />
        {importResult && (
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            message={`导入完成: 新入库 ${importResult.imported} 条, 重复跳过 ${importResult.duplicated} 条`}
          />
        )}
        <Form form={form} layout="vertical">
          <Form.Item
            name="sku_id"
            label="SKU (归属商品)"
            rules={[{ required: true, message: '请选择 SKU' }]}
          >
            <Select
              options={skuOptions}
              placeholder={skuOptions.length ? '选择 SKU' : '该 App 暂无 SKU (先去「商业 SKU」页创建)'}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item
            name="codes_text"
            label="卡密 (一行一码)"
            rules={[{ required: true, message: '请粘贴卡密' }]}
          >
            <Input.TextArea
              rows={10}
              placeholder={'ABCD-1234-EFGH\nIJKL-5678-MNOP\n...'}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* ---- 同步商品折扣价 (card-api disclist, 只读) ---- */}
      <Modal
        title="card-api 授权商品 + 折后价"
        open={discOpen}
        onCancel={() => setDiscOpen(false)}
        footer={null}
        width={720}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="供应商授权商品的产品编码与折后单价 (只读). 复制 HHT/YHT 编码与折后价, 到「商业 SKU」页填入 card-api 履约参数 (product_code / agreed_price). ⚠️ 供应商限流 10s/次, 请勿频繁刷新."
        />
        <Table<CardKeyDiscListItem>
          rowKey={(r) => r.HCamiCode || r.YCamiCode || r.CardName}
          loading={discLoading}
          dataSource={discItems}
          size="small"
          pagination={false}
          columns={[
            { title: '商品名', dataIndex: 'CardName' },
            { title: '面值', dataIndex: 'CardPrice', align: 'right' },
            {
              title: '官方卡 (HHT)',
              key: 'hht',
              render: (_, r) =>
                r.HCamiCode ? (
                  <span>
                    <Tag>{r.HCamiCode}</Tag>
                    <Typography.Text strong>{r.HCamiPrice}</Typography.Text>
                  </span>
                ) : (
                  <Typography.Text type="secondary">-</Typography.Text>
                ),
            },
            {
              title: '自生成卡 (YHT)',
              key: 'yht',
              render: (_, r) =>
                r.YCamiCode ? (
                  <span>
                    <Tag>{r.YCamiCode}</Tag>
                    <Typography.Text strong>{r.YCamiPrice}</Typography.Text>
                  </span>
                ) : (
                  <Typography.Text type="secondary">-</Typography.Text>
                ),
            },
          ]}
        />
      </Modal>
    </>
  );
}
